<template>
	<div>
		<el-switch v-loading="operationInProgress" @click.native="toggleActivationState()" :style="customStyle"
			:value="isActive" active-text="" :disabled="disabled" :inactive-text="activeVersionLabel">
		</el-switch>
		<el-dialog :title="!this.isActive ? 'Start Workflow' : 'Stop Workflow'" :visible.sync="showWorkflowStatusModal"
			width="420px" :close-on-click-modal="false" @click.stop :append-to-body="true">
			<div @click.stop>
				<div class="dialog-field">
					<label class="dialog-label" style="margin-bottom: 8px; display: block;">Select version</label>

					<el-select v-if="versions" v-model="selectedWorkflowVersion" placeholder="Select version"
						style="width: 100%">
						<el-option v-for="v in versions" :key="v.versionId" :label="v.versionName && v.versionName !== v.version
							? `${v.versionName} (${v.version})`
							: v.version
							" :value="v.versionId" />
					</el-select>
				</div>

				<span slot="footer" style="margin-top: 28px; display: flex; justify-content: flex-end;">
					<el-button @click="showWorkflowStatusModal = false">
						Cancel
					</el-button>
					<el-button type="primary" @click="confirmToggleWorkflow" :disabled="!selectedWorkflowVersion">
						{{ !this.isActive ? 'Start Workflow' : 'Stop Workflow' }}
					</el-button>
				</span>
			</div>
		</el-dialog>
	</div>
</template>

<script>
import { mapActions } from "vuex";
export default {
	computed: {
		isActive() {
			return this.workflow?.isActive === true;
		},
		activeVersionLabel() {
			if (!this.isActive) return "Off";

			if (this.activeVersion?.version && this.workflowListActive) {
				return `On (${this.activeVersion.version})`;
			}

			return "On";
		},
	},
	mounted() {
		this.loadWorkflowVersions();
	},
	watch: {
		selectedWorkflowVersion(newVersionId) {
			if (!newVersionId || !this.versions?.length) {
				this.selectedWorkflowLastUpdatedAt = null;
				return;
			}

			const selected = this.versions.find(
				v => v.versionId === newVersionId
			);

			this.selectedWorkflowLastUpdatedAt = selected?.updatedAt || null;
		},
		workflow: {
			deep: true,
			handler() {
				this.loadWorkflowVersions();
			},
		},
	},
	props: {
		customStyle: String,
		workflow: Object,
		simplifiedError: Boolean,
		workflowListActive: Boolean,
		disabled: Boolean,
		refreshVersions: Function,
	},
	data() {
		return {
			operationInProgress: false,
			showWorkflowStatusModal: false,
			selectedWorkflowVersion: null,
			selectedWorkflowLastUpdatedAt: null,
			versions: [],
			activeVersion: null,
		};
	},
	methods: {
		...mapActions(["changesToUsageCreditMETHOD", "fetchWorkflowVersionsMETHOD"]),
		async confirmToggleWorkflow() {
			this.showWorkflowStatusModal = false;

			await this.toggleWorkflow();
		},
		async loadWorkflowVersions() {
			if (!this.workflow?.id && !this.workflow?.workflowId) return;

			this.versions = await this.fetchWorkflowVersionsMETHOD(
				this.workflow.id || this.workflow.workflowId
			);

			this.activeVersion = this.versions?.find(v => v.isActive) || null;
		},
		async toggleWorkflow() {
			const versionId = this.selectedWorkflowVersion;
			if (this.disabled) {
				return;
			}
			this.operationInProgress = true;
			const result = await this.httpHelper("RUNTIME", "POST", "workflow/toggleActivationState", {
				workflowId: this.workflow.id || this.workflow.workflowId,
				versionId: versionId && this.workflowListActive ? versionId : !this.workflowListActive ? this.workflow.versionId : "",
				workflowLastUpdated: this.selectedWorkflowLastUpdatedAt || this.workflow.updatedAt,
			});
			setTimeout(async () => {
				this.operationInProgress = false;
				this.changesToUsageCreditMETHOD();
				if (!result?.error) {
					this.workflow.updatedAt = result.response.updatedAt;
					this.workflow.isActive = result.response.isActive;
					this.$emit("updateReferenceWorkflow", this.workflow);
					this.$notify({
						title: "Success",
						message: `Workflow ${result.response.isActive ? "started" : "stopped"}`,
						type: "success",
						position: "bottom-left",
					});

					if (typeof this.refreshVersions === "function") {
						await this.refreshVersions();
					}
				} else {
					let error = this.recursivelyParseError(result.response?.data?.error) || "Unknown error";
					if (typeof error === "string") {
						error = error.replace(/node/g, "integration");
					}
					this.$emit("highlightNodeErrorId", {
						nodeId: result.response?.data?.error?.nodeId || null,
						error: error,
					});
					if (result?.status !== 401) {
						if (result.response?.data?.noCredit) {
							this.$notify({
								title: "Warning",
								message: result.response?.data?.error,
								type: "warning",
								position: "bottom-left",
							});
							return;
						}
						if (this.simplifiedError) {
							this.$notifyError(
								this,
								"Unable to start workflow. Integration associated error. Attempt to start within editor for more detail"
							);
						} else {
							this.$notifyError(this, "Unable to start workflow. Associated error: " + error);
						}
					}
				}
			}, 500);
		},
		async toggleActivationState() {
			const needsSimpleConfirm =
				this.isActive ||
				!this.workflowListActive ||
				!this.versions ||
				this.versions.length === 0;

			if (needsSimpleConfirm) {
				this.$confirm(`Do you wish to ${!this.isActive ? "start" : "stop"} this workflow. Continue?`, "Warning", {
					confirmButtonText: "OK",
					cancelButtonText: "Cancel",
					type: "warning",
				})
					.then(() => {
						this.toggleWorkflow();
					})
					.catch(() => { });
				return;
			}
			const active =
				this.versions.find(v => v.isActive) || this.versions[0];
			this.selectedWorkflowLastUpdatedAt = active?.updatedAt

			this.showWorkflowStatusModal = true;
		},
		recursivelyParseError(error) {
			if (error?.message) {
				if (error.message.error) {
					return this.recursivelyParseError(error.message.error);
				} else {
					return error.message;
				}
			}
		},
	},
};
</script>

<style scoped></style>
